import React, { useState } from 'react';
import {
  Menu,
  MenuItem,
  Typography,
  Box,
  Badge,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  TextField,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useSelector } from 'store/hooks';
import { useAppState } from 'store/Provider';
import { capitalize } from 'helpers/format';
import { loadCustomerAction, loadRolesAction } from 'store/actions/appActions';
import Customer from 'types/entities/Customer';
import SearchIcon from '@mui/icons-material/Search';

const CustomerSelect: React.FC = () => {
  const { dispatch } = useAppState();
  const appState = useSelector((state) => state.app);
  const [search, setSearch] = useState('');
  const customers: Customer[] = appState.customers
    ? [...appState.customers].sort((a, b) => a.name.localeCompare(b.name))
    : [];


  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(appState.customer || null);
  const filteredCustomers = customers?.filter((customer) =>
    customer.name.toLowerCase().includes(search.toLowerCase())
  );
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event: React.MouseEvent<HTMLElement>, customer?: Customer) => {
    if ('key' in event && event.key === ' ') {
      return;
    }
    setAnchorEl(null);

    if (!customer) return; // just close menu if no customer selected

    const previous_customer: string | null = localStorage.getItem('active_customer');
    if (previous_customer === JSON.stringify(customer)) {
      return;
    }
    // Save active customer to localStorage
    localStorage.setItem('active_customer', JSON.stringify(customer));

    // Dispatch actions
    dispatch(loadCustomerAction(customer));
    dispatch(loadRolesAction());
    localStorage.setItem('navigation_screen', JSON.stringify('customer')); // Clear active role on customer change
  };

  const buttonLabel = selectedCustomer ? capitalize(selectedCustomer.name) : appState.customer?.name || '';

  return (
    <>
      {/* Topbar display */}
      <Box
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          color: 'text.primary',
        }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          {buttonLabel}
        </Typography>
        <KeyboardArrowDownIcon sx={{ ml: 0.5 }} />
      </Box>

      {/* Dropdown Menu */}
      <Menu
        id="customer-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={() => {
          setSearch('');
          setAnchorEl(null)
        }}
        onKeyDown={(e) => {
          if (e.key === ' ') {
            // This stops the event from bubbling up to the MenuItem's internal selection logic
            e.stopPropagation();
            // This prevents the page from scrolling (the default behavior of Space)
            e.preventDefault();
          }
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 240,
            maxHeight: 340,
            borderRadius: 2,
            mt: 1,
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          },
        }}
      >
        {/* Header */}
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Switch Client
          </Typography>
        </Box>
        {/* <Divider /> */}

        <Box sx={{ p: 1.5, borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search Client..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === ' ') {
                e.stopPropagation();
              }
            }}
            autoFocus
            onClick={(e) => e.stopPropagation()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f5f5f5',
              },
            }}
          />
        </Box>

        {/* Customer list */}
        <Box>
          {filteredCustomers?.map((item) => {
            const isSelected = selectedCustomer?.id === item.id;
            return (
              <MenuItem
                key={item.id}
                onClick={(e) => handleClose(e, item)}
                onKeyDown={(e) => {
                  if (e.key === ' ') {
                    e.stopPropagation(); // Stops the Menu from treating Space as a "Close/Select" action
                  }
                }}
                selected={isSelected}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 1.2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {isSelected && (
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <CheckIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                  )}
                  <ListItemText
                    primaryTypographyProps={{
                      fontWeight: isSelected ? 600 : 400,
                      variant: 'body2',
                    }}
                  >
                    {capitalize(item.name)}
                  </ListItemText>
                </Box>

                {/* Example badge (replace item.count with your real data field) */}
                {item?.count && (
                  <Badge
                    badgeContent={item?.count}
                    color="error"
                    sx={{
                      '& .MuiBadge-badge': {
                        right: -6,
                        top: 2,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        height: 18,
                        minWidth: 18,
                      },
                    }}
                  />
                )}
              </MenuItem>
            );
          })}
        </Box>
      </Menu>
    </>
  );
};

export default CustomerSelect;
